import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import {
  FaClock, FaSyringe, FaStethoscope, FaUserMd, FaCut,
  FaArrowRight, FaArrowLeft, FaCalendarAlt, FaCat, FaDog,
  FaKiwiBird, FaCheck
} from 'react-icons/fa';

// TypeScript için Email.js window tanımlaması
declare global {
  interface Window {
    emailjs: typeof emailjs;
  }
}

const Randevu: React.FC = () => {
  // State yönetimi
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    petName: '',
    petType: '',
    reason: '',
  });

  // Servis seçenekleri
  const services = [
    {
      id: 'asilama',
      title: 'Aşılama',
      icon: FaSyringe,
      duration: '45 dakika',
      description: 'Koruyucu sağlık hizmetleri kapsamında aşılama',
      color: 'bg-blue-500'
    },
    {
      id: 'muayene',
      title: 'Genel Muayene',
      icon: FaStethoscope,
      duration: '30 dakika',
      description: 'Genel sağlık kontrolü ve muayene',
      color: 'bg-green-500'
    },
    {
      id: 'kontrol',
      title: 'Kontrol Muayene',
      icon: FaUserMd,
      duration: '20 dakika',
      description: 'Tedavi sonrası kontrol muayenesi',
      color: 'bg-purple-500'
    },
    {
      id: 'cerrahi',
      title: 'Cerrahi İşlem',
      icon: FaCut,
      duration: '60+ dakika',
      description: 'Her türlü cerrahi operasyon',
      color: 'bg-red-500'
    }
  ];

  // Animasyon varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  // Form değişiklik yönetimi
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form gönderimi
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedServiceData = services.find(s => s.id === selectedService);
    
    const emailBody = `
      Yeni Randevu Talebi

      Hasta Sahibi Bilgileri:
      Ad Soyad: ${formData.name}
      E-posta: ${formData.email}
      Telefon: ${formData.phone}

      Hasta Bilgileri:
      Hasta Adı: ${formData.petName}
      Hasta Türü: ${formData.petType}

      Randevu Detayları:
      Hizmet: ${selectedServiceData?.title}
      Tarih: ${selectedDate}
      Saat: ${selectedTime}

      Açıklama:
      ${formData.reason}
    `;

    try {
      // Email.js kullanarak mail gönderme
      const templateParams = {
        to_email: "klinik@email.com", // Kliniğin email adresi
        from_name: formData.name,
        from_email: formData.email,
        subject: "Yeni Randevu Talebi",
        message: emailBody,
      };

      await window.emailjs.send(
        "service_xxxxxxx", // Email.js service ID'nizi buraya girin
        "template_xxxxxxx", // Email.js template ID'nizi buraya girin
        templateParams,
        "public_key_xxxxxxx" // Email.js public key'inizi buraya girin
      );

      // Başarılı mesajı göster
      alert("Randevunuz başarıyla oluşturuldu! Onay maili size gönderilecektir.");
      
      // Formu sıfırla
      setSelectedService(null);
      setSelectedDate("");
      setSelectedTime("");
      setFormData({
        name: "",
        email: "",
        phone: "",
        petName: "",
        petType: "",
        reason: "",
      });
      setStep(1);
      
    } catch (error) {
      console.error("Mail gönderme hatası:", error);
      alert("Randevu oluşturulurken bir hata oluştu. Lütfen daha sonra tekrar deneyin.");
    }
  };

  // Zaman dilimlerini oluştur
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      slots.push(`${hour}:00`);
      if (hour !== 17) slots.push(`${hour}:30`);
    }
    return slots;
  };

  // İlerleme çubuğu
  const ProgressBar = () => (
    <div className="w-full max-w-3xl mx-auto mb-8">
      <div className="flex justify-between">
        {[1, 2, 3].map((stepNumber) => (
          <div key={stepNumber} className="flex flex-col items-center">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                step >= stepNumber ? 'bg-primary text-white' : 'bg-gray-200'
              }`}
            >
              {step > stepNumber ? <FaCheck /> : stepNumber}
            </div>
            <span className="text-sm mt-2 text-gray-600">
              {stepNumber === 1 && 'Hizmet Seçimi'}
              {stepNumber === 2 && 'Tarih & Saat'}
              {stepNumber === 3 && 'Bilgiler'}
            </span>
          </div>
        ))}
      </div>
      <div className="relative mt-4">
        <div className="absolute top-0 h-1 bg-gray-200 w-full rounded-full"></div>
        <div
          className="absolute top-0 h-1 bg-primary transition-all duration-300 rounded-full"
          style={{ width: `${((step - 1) / 2) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="h-24"></div>
      <motion.div 
        className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary text-white py-16 px-4 rounded-3xl mx-4 mb-12 shadow-2xl overflow-hidden"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Dekoratif arka plan elementleri */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 -top-48 -right-48 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute w-96 h-96 -bottom-48 -left-48 bg-secondary/20 rounded-full blur-xl"></div>
          <div className="absolute w-72 h-72 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary-light/10 rounded-full blur-lg"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight mb-6">
            Online Randevu{" "}
            <span className="text-secondary bg-white/10 px-4 py-2 rounded-lg inline-block mt-2">
              Sistemi
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
            Sevimli dostunuz için kolayca randevu alın. Profesyonel veteriner ekibimiz sizlere yardımcı olmak için hazır.
          </p>
        </div>
      </motion.div>

      <ProgressBar />

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">Hizmet Seçimi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => setSelectedService(service.id)}
                  className={`bg-white rounded-xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 ${
                    selectedService === service.id
                      ? 'ring-2 ring-primary shadow-lg'
                      : 'shadow-md hover:shadow-xl'
                  }`}
                >
                  <div className={`w-12 h-12 ${service.color} rounded-full flex items-center justify-center text-white mb-4`}>
                    <service.icon size={24} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <FaClock className="mr-2" />
                    {service.duration}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => selectedService && setStep(2)}
                disabled={!selectedService}
                className="bg-primary text-white px-8 py-3 rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors"
              >
                İleri
                <FaArrowRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">Tarih ve Saat Seçimi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FaCalendarAlt className="mr-2 text-primary" />
                  Tarih Seçimi
                </h3>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {selectedDate && (
                  <p className="text-sm text-green-600 mt-2 flex items-center">
                    <FaCheck className="mr-1" /> Tarih seçildi
                  </p>
                )}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FaClock className="mr-2 text-primary" />
                  Saat Seçimi
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {generateTimeSlots().map((time) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedTime === time
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                {selectedTime && (
                  <p className="text-sm text-green-600 mt-2 flex items-center">
                    <FaCheck className="mr-1" /> Saat seçildi
                  </p>
                )}
              </div>
            </div>
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg flex items-center hover:bg-gray-300 transition-colors"
              >
                <FaArrowLeft className="mr-2" />
                Geri
              </button>
              <button
                onClick={() => {
                  if (selectedDate && selectedTime) {
                    setStep(3);
                  }
                }}
                disabled={!selectedDate || !selectedTime}
                className={`px-8 py-3 rounded-lg flex items-center transition-colors ${
                  selectedDate && selectedTime
                    ? 'bg-primary text-white hover:bg-primary-dark'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                İleri
                <FaArrowRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">Kişisel Bilgiler</h2>
            <div className="bg-white rounded-xl p-8 shadow-md">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-posta
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Evcil Hayvan Adı
                    </label>
                    <input
                      type="text"
                      name="petName"
                      value={formData.petName}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evcil Hayvan Türü
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: 'kedi', icon: FaCat, label: 'Kedi' },
                      { value: 'kopek', icon: FaDog, label: 'Köpek' },
                      { value: 'kus', icon: FaKiwiBird, label: 'Kuş' }
                    ].map((pet) => (
                      <button
                        type="button"
                        key={pet.value}
                        onClick={() => setFormData(prev => ({ ...prev, petType: pet.value }))}
                        className={`p-4 rounded-lg flex flex-col items-center transition-colors ${
                          formData.petType === pet.value
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <pet.icon size={24} className="mb-2" />
                        {pet.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Şikayet / Açıklama
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  ></textarea>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg flex items-center hover:bg-gray-300 transition-colors"
                  >
                    <FaArrowLeft className="mr-2" />
                    Geri
                  </button>
                  <button
                    type="submit"
                    className="bg-primary text-white px-8 py-3 rounded-lg flex items-center hover:bg-primary-dark transition-colors"
                  >
                    Randevu Oluştur
                    <FaCheck className="ml-2" />
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Randevu; 